import type { AskArmSettled, KarlPlanSnapshot } from "./types";

const movedSource = (
  before: KarlPlanSnapshot,
  after: KarlPlanSnapshot,
  label: string,
): boolean => !Object.is(before.placed[label], after.placed[label]);

const placedSource = (
  before: KarlPlanSnapshot,
  after: KarlPlanSnapshot,
  name: string,
): boolean =>
  Object.keys(after.added).some(
    (label) => after.added[label]?.name === name && !(label in before.added),
  );

const zoneCountFor = (snapshot: KarlPlanSnapshot, label: string): number =>
  snapshot.zones.filter((zone) => zone.sourceLabel === label).length;

export const askArmSettled: AskArmSettled = ({ arm, before, after }) => {
  switch (arm.kind) {
    case "moveSource": {
      if (movedSource(before, after, arm.label)) return "committed";
      // Arming sets `grabbedLabel`; only the way back out empty-handed is a cancel.
      if (before.grabbedLabel === arm.label && after.grabbedLabel === null)
        return "cancelled";
      return "pending";
    }
    case "placeSource": {
      if (placedSource(before, after, arm.name)) return "committed";
      if (before.addingName === arm.name && after.addingName === null)
        return "cancelled";
      return "pending";
    }
    // The three box arms differ only in what the commit then DOES with the
    // catch; the plan handshake that settles them is the same one.
    case "drawBox":
    case "boxAssign":
    case "boxRemove": {
      // Checked before the cancel: a completed box flips both flags in one
      // render, and reading that as a cancel would drop what he just caught.
      if (!before.hasDeviceSelection && after.hasDeviceSelection)
        return "committed";
      if (
        before.isSelectingDevices &&
        !after.isSelectingDevices &&
        !after.hasDeviceSelection
      )
        return "cancelled";
      return "pending";
    }
    case "pickRoute": {
      // He answers by isolating a run. Any change to a real circuit is the
      // answer; clearing the selection is him backing out of the question.
      if (after.selectedCircuit && after.selectedCircuit !== before.selectedCircuit)
        return "committed";
      if (before.selectedCircuit && !after.selectedCircuit) return "cancelled";
      return "pending";
    }
    case "drawZone": {
      // Closing a zone writes it and drops the draft in one render; the write is the answer.
      if (zoneCountFor(after, arm.label) > zoneCountFor(before, arm.label))
        return "committed";
      if (before.drawingZoneFor === arm.label && after.drawingZoneFor === null)
        return "cancelled";
      return "pending";
    }
    case "pickFeederFor": {
      if (before.pickingFeederFor === arm.category && after.pickingFeederFor === null) {
        return after.boardFeeds !== before.boardFeeds ? "committed" : "cancelled";
      }
      return "pending";
    }
    case "pickUpstreamFor": {
      if (before.pickingUpstreamFor === arm.toLabel && after.pickingUpstreamFor === null) {
        return after.feeders !== before.feeders ? "committed" : "cancelled";
      }
      return "pending";
    }
    case "pickMainBoard": {
      if (after.mainBoardLabel && after.mainBoardLabel !== before.mainBoardLabel)
        return "committed";
      if (before.pickingMainBoard && !after.pickingMainBoard) return "cancelled";
      return "pending";
    }
    default: {
      const unreachable: never = arm;
      return unreachable;
    }
  }
};
